import type { CloudEnvironmentDiscoverer } from '../types/CloudEnvironmentDiscoverer';
import type { CloudEnvironmentResolver } from '../types/CloudEnvironmentResolver';
import type { CloudEnvironmentStorage } from '../types/CloudEnvironmentStorage';
import type { CloudEnvironment } from '../types/CloudEnvironment';
import { getDomainFromEmailAddress } from '../utils/getDomainFromEmailAddress';
import { isSovereignEnvironment } from '../utils/isSovereignEnvironment';
import { getOrigin } from 'owa-url/lib/getOrigin';

export class CloudEnvironmentResolverImpl implements CloudEnvironmentResolver {
    private readonly activeDomainResolutions = new Map<string, Promise<CloudEnvironment>>();
    private readonly appLoadOrigin: string;

    constructor(
        private readonly storage: CloudEnvironmentStorage,
        private readonly discoverer: CloudEnvironmentDiscoverer
    ) {
        this.appLoadOrigin = getOrigin();
    }

    async resolveConfigForEmailAddress(emailAddress: string): Promise<CloudEnvironment> {
        const domain = getDomainFromEmailAddress(emailAddress);
        if (!domain) {
            throw new Error('Invalid email address: no domain found');
        }

        const activeDomainResolution = this.activeDomainResolutions.get(domain);
        if (activeDomainResolution) {
            return activeDomainResolution;
        }

        const domainResolution = this.resolveForDomainImpl(domain).finally(() =>
            this.activeDomainResolutions.delete(domain)
        ); // always clean up active resolutions, even if they fail

        this.activeDomainResolutions.set(domain, domainResolution);
        return domainResolution;
    }

    async refreshKnownConfigs(): Promise<void> {
        const domainsToRefresh = await this.storage.getDomainsToRefresh();

        // Best-effort to refresh eligible domains; swallow any errors, they will
        // be logged within the discoverer
        await Promise.allSettled(
            domainsToRefresh.map(async domain => {
                const discovery = await this.discoverer.discoverConfigForDomain(domain);
                await this.storage.putConfigForDomain(
                    domain,
                    discovery.cloudEnvironment,
                    discovery.metadata
                );
            })
        );
    }

    private async resolveForDomainImpl(domain: string): Promise<CloudEnvironment> {
        const cloudEnv = await this.coordinateDomainLookup(domain);

        if (!isSovereignEnvironment(cloudEnv)) {
            // In non-sovereign environments there are several valid Outlook Web origins that can be used to load
            // the app and send requests to (e.g. outlook.office.com, outlook.office365.com, outlook.cloud.microsoft);
            // our cloud env discovery can only provide the preferred origin for the environment, so if we're
            // non-sovereign we simply adopt the origin we loaded the app from (which will always be non-sovereign).
            // This is particularly relevant in OWA, where the user specifies the origin the load from.
            cloudEnv.outlookWebOrigin = this.appLoadOrigin;
        }

        return cloudEnv;
    }

    private async coordinateDomainLookup(domain: string): Promise<CloudEnvironment> {
        const cachedCloudEnv = await this.storage.getConfigForDomain(domain);
        if (cachedCloudEnv) {
            return cachedCloudEnv;
        }

        const discovery = await this.discoverer.discoverConfigForDomain(domain);
        await this.storage.putConfigForDomain(
            domain,
            discovery.cloudEnvironment,
            discovery.metadata
        );

        return discovery.cloudEnvironment;
    }
}
